import * as React from "react";
import { usePlayerSettings, useSocialCoachSession } from "@/utils";
import { PlayersServices } from "@/services/bedrock";
import { HashTagsServices } from "@/services";
import { CoachSettings } from "@/interfaces";
import { AppContext } from "@/providers";

import { useQuery } from "@tanstack/react-query";

export interface BuiltCaptionProps {
  defaultMessage: string | undefined;
  setMessage: (message: string) => void;
  hashtags: string;
  addShortLink: boolean;
  addDisclaimer: boolean;
  addDefaultHashtags: boolean;
}

export const useBuiltCaption = ({
  defaultMessage,
  setMessage,
  hashtags,
  addShortLink,
  addDisclaimer,
  addDefaultHashtags,
}: BuiltCaptionProps) => {
  const { userContext } = React.useContext(AppContext);
  const { playerId, coachId } = useSocialCoachSession();

  const { hasSetting } = usePlayerSettings(playerId);
  const alwaysShowDisclaimer = hasSetting(CoachSettings.COMPLIANCE_DISCLAIMER, false);
  const allowCustomHashtags = hasSetting(CoachSettings.PLAYER_HASHTAG, true);

  const { data: player, isPending: playerLoading } = useQuery({
    queryKey: ["player", playerId, "shortLink"],
    queryFn: () => PlayersServices.getPlayer(playerId!, ["shortLink"]),
    enabled: !!playerId,
  });
  const playerShortLink = player?.shortLink?.url;

  const { data: tags, isPending: tagsLoading } = useQuery({
    queryKey: ["hashtags", playerId, coachId],
    queryFn: () => HashTagsServices.get(playerId!, coachId),
    enabled: !!playerId,
  });
  const playerHashtags = React.useMemo(() => tags?.map(h => h.content) || [], [tags]);

  const playerDisclaimer = userContext?.profile?.complianceDisclaimer;

  const keepFirstOccurrence = (text: string, target: string | undefined): string => {
    if (!target || !target.trim()) return text;

    const words = target.trim().split(/\s+/);
    const escapedWords = words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const regex = new RegExp(escapedWords.join("\\s+"), "gi");

    let occurrences = 0;
    return text.replace(regex, match => {
      occurrences++;
      return occurrences === 1 ? match : "";
    });
  };

  React.useEffect(() => {
    const parts = [defaultMessage || ""];

    if (addShortLink && playerShortLink && playerShortLink.length > 0) {
      parts.push(playerShortLink);
    }

    const tags = new Set<string>([]);
    const downcaseTags = new Set<string>([]);
    const addTag = (tag: string) => {
      if (tag && tag.length > 0 && !downcaseTags.has(tag.toLowerCase()) && !defaultMessage?.includes(tag)) {
        const tagString = tag.startsWith("#") ? tag : "#" + tag;
        tags.add(tagString);
        downcaseTags.add(tagString.toLowerCase());
      }
    };
    if (hashtags && hashtags.length > 0) {
      hashtags.split(" ").forEach(tag => addTag(tag));
    }
    if (addDefaultHashtags && allowCustomHashtags && playerHashtags && playerHashtags.length > 0) {
      playerHashtags.forEach(tag => addTag(tag));
    }

    if (tags.size > 0) {
      parts.push([...tags].join(" "));
    }

    if ((alwaysShowDisclaimer || addDisclaimer) && playerDisclaimer && playerDisclaimer.length > 0) {
      parts.push(playerDisclaimer);
    }

    let finalMessage = parts.filter(Boolean).join("\n\n");
    if (playerShortLink && playerShortLink.length > 0)
      finalMessage = keepFirstOccurrence(finalMessage, playerShortLink);
    if (playerDisclaimer && playerDisclaimer.length > 0)
      finalMessage = keepFirstOccurrence(finalMessage, playerDisclaimer);

    setMessage(finalMessage.trim());
  }, [
    addShortLink,
    playerShortLink,
    addDefaultHashtags,
    allowCustomHashtags,
    playerHashtags,
    addDisclaimer,
    alwaysShowDisclaimer,
    playerDisclaimer,
    defaultMessage,
  ]);

  return {
    builderLoading: playerLoading || tagsLoading,
  };
};
